import "../../assets/less/chosen/chosen.less";
import "../../assets/css/app.css";
import React, { useMemo, useEffect } from "react";
import { BraintreeProvider } from "../../braintree/BraintreeProvider";
import MainStateContext from "../../state/mainStateContext";
import { determineInitialState } from "../../state/mainInitialState";
import reducer from "../../state/mainStateReducer";
import Checkout from "../Shared/Checkout";
import StepContainer from "../Shared/StepContainer";
import AppOverlay from "../App/AppOverlay";
import PhotoModerationWrapper from "./photoModeration/PhotoModerationWrapper.js";
import {
  checkIsThankyouPage,
  getCheckoutProduct,
  getIsKioskMode,
  getUrlWithoutKioskParameter,
  wvusLogger
} from "../../helpers";
import OrderConfirmationWrapper from "./orderConfirmation/OrderConfirmationWrapper";
import { Group } from "../Shared/Group";
import { PaymentFirstHeader } from "../Shared/PaymentFirstHeader";
import appSettings from "../../settings/appSettings";
import { useTokenConfig } from "../../hooks/useTokenConfig.js";
import QrCode from "../Shared/QrCode.js";
import useClearStorage from "../../hooks/useClearStorage.js";
import { CheckoutData, ChosenAppProps, LineItem, Product } from "../../types";
import { PhraseKey, Say } from "../../languages";
import { OrderRequestProvider } from "../../orderRequest/OrderRequestProvider.js";

export default function ChosenApp(props: ChosenAppProps) {
  const { opts = {} } = props || {};
  const { checkoutFlow, allowMultipleQuantity } = appSettings;
  const initialState = determineInitialState();
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const hideStepTitles = ["PaymentFirst"].includes(checkoutFlow);
  const stateContextMemo = useMemo(() => {
    const findProductById = (productId: string): Product => {
      return state.products.find(
        (product: Product) => product.productId === productId || product.product_id === productId
      );
    };

    return {
      state,
      dispatch,
      setSubmitMessagePhraseKey: (phrase: PhraseKey) => {
        dispatch({ type: "SET_SUBMIT_MESSAGE_PHRASE_KEY", payload: phrase });
      },
      toggleOverlay: (overlayActive = state.appOverlayActive) => {
        dispatch({ type: "TOGGLE_APP_OVERLAY", payload: overlayActive });
      },
      findProductById,
      getProductQuantity: (productId: string): number | undefined => {
        const selectedProduct = findProductById(productId);
        if (!selectedProduct || (!selectedProduct.qty && !selectedProduct.quantity)) {
          return 0;
        } else {
          return selectedProduct.quantity || selectedProduct.qty;
        }
      }
    };
  }, [state, dispatch]);

  const isKioskMode = getIsKioskMode();
  const appUrlWithoutKiosk = getUrlWithoutKioskParameter();

  // Clear cookies, session, and local storage in kiosk mode
  useClearStorage(isKioskMode);

  useEffect(() => {
    const preAppLoader = document.querySelector(".wvus-streamlined-loader");
    if (preAppLoader) {
      preAppLoader.remove();
    }
  }, []);

  const { step1, step2, thankYouStep } = state;

  const tokenExConfigQuery = useTokenConfig();

  const addProductsListener = (event: CustomEvent) => {
    if (event?.detail?.products?.length) {
      // TODO: Add Products Validation
      const payload = event.detail.products;

      // Validate Product
      dispatch({ type: "ADD_PRODUCTS", payload: payload });
    } else {
      const error = new Error("No products found.");
      console.error(error);
      wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    }
  };

  useEffect(() => {
    window.addEventListener("wvcheckout:addProducts", addProductsListener);

    return () => {
      window.removeEventListener("wvcheckout:addProducts", addProductsListener);
    };
  }, [state.products]);

  useEffect(() => {
    // Add Product When Payment First And Go To Step 2
    const isThankyouPage = checkIsThankyouPage();

    if (!isThankyouPage) {
      // Assert campaign for Chosen events only on checkout page, not on thank you page.
      const campaignScheduleCode = window?.wvusChosenEventData?.[0]?.CampaignScheduleCode;
      if (campaignScheduleCode) {
        window.wvusCSSC = window.wvusCSSC || [];
        window.wvusCSSC.push({ origin: "chosen_event_config", value: `${campaignScheduleCode}` });
      } else {
        // Log warning if chosen event CSSC value is empty or missing
        wvusLogger.log(
          `Chosen event CSSC value is empty or missing. wvusChosenEventData: ${JSON.stringify(window?.wvusChosenEventData)}`,
          wvusLogger.getLogLevelNames().WARN
        );
      }

      if (checkoutFlow === "PaymentFirst" && !state?.products?.legnth) {
        const checkoutProduct = getCheckoutProduct({
          eventNumber: state?.eventData?.EventNumber,
          keyword: state?.eventData?.SmsKeyword,
          cobrand: state?.cobrand
        });
        const payload = [checkoutProduct];

        dispatch({ type: "ADD_PRODUCTS", payload: payload });
        dispatch({ type: "SET_STEP2", payload: { enabled: true } });
      }
    }
  }, []);

  useEffect(() => {
    /*
     * Scroll to step 2 when enabled. This helps advance the user to the next step.
     */
    if (state?.step2?.enabled === true) {
      setTimeout(() => {
        window.document.querySelector(".chosen__step-2")?.scrollIntoView({
          behavior: "smooth"
        });
      }, 200);
    }
  }, [state?.step2?.enabled]);

  const onCheckoutLineItemsMap = (
    lineItems: LineItem[],
    checkoutData: CheckoutData
  ): LineItem[] => {
    const { photoConsent, showPhotoConsentOption } = checkoutData;

    if (!showPhotoConsentOption) {
      return lineItems;
    }

    const photoConsentLineMeta = photoConsent ? "true" : "false";

    return lineItems.map((item) => {
      return {
        ...item,
        lineMeta: {
          ...item.lineMeta,
          wvus_product_streamlined_failover: state?.rcdsFailover ? "true" : "false",
          wvus_product_event_donor_photo_consent: photoConsentLineMeta
        }
      };
    });
  };

  const checkoutFlowClass =
    !allowMultipleQuantity && checkoutFlow === "PaymentFirst" ? checkoutFlow : "";

  return (
    <MainStateContext.Provider value={stateContextMemo}>
      <OrderRequestProvider>
        <BraintreeProvider isKioskMode={isKioskMode}>
          <AppOverlay />
          {isKioskMode && (
            <div className="kiosk-mode-container">
              <h2 className="kiosk-mode-text">
                <Say phrase="kiosk_mode.heading" />
              </h2>
              <p className="kiosk-mode-paragraph-text">
                <Say phrase="kiosk_mode.intro_paragraph" />
              </p>
              <QrCode data={appUrlWithoutKiosk} width={150} height={150} kiosk={isKioskMode} />
              <p className="kiosk-mode-text">
                <Say phrase="kiosk_mode.scan_instruction" />
              </p>
            </div>
          )}
          <div className={`App ChosenApp ${checkoutFlowClass}`}>
            <StepContainer {...step1} hideTitle={hideStepTitles} className="chosen__step-1">
              <Group visible={checkoutFlow === "PhotoFirst"}>
                <PhotoModerationWrapper />
              </Group>
              <Group visible={checkoutFlow === "PaymentFirst"}>
                <PaymentFirstHeader />
              </Group>
            </StepContainer>

            <StepContainer {...step2} hideTitle={hideStepTitles} className="chosen__step-2">
              <Checkout
                tokenExConfigQuery={tokenExConfigQuery}
                onCheckoutLineItemsMap={onCheckoutLineItemsMap}
                formWrapperClassName="checkout__wrapper"
                opts={opts}
                ctaPhraseKey="chosen.submission.cta"
                designatedProductId="460964"
                isKioskMode={isKioskMode}
              />
            </StepContainer>

            <StepContainer {...thankYouStep}>
              <OrderConfirmationWrapper />
            </StepContainer>
          </div>
        </BraintreeProvider>
      </OrderRequestProvider>
    </MainStateContext.Provider>
  );
}
